import { Panel, useReactFlow } from 'reactflow';

import 'reactflow/dist/style.css';

import styled from 'styled-components';

import { Button } from '@components/button';
import { useLocalStorageState } from '@components/component-helpers';
import { IconAiGradient } from '@components/icons/ai-gradient';
import { IconHelp } from '@components/icons/help';
import { IconZoomAll } from '@components/icons/zoom-all';
import { IconZoomIn } from '@components/icons/zoom-in';
import { IconZoomOut } from '@components/icons/zoom-out';
import { PopoverButton } from '@components/popover-button';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useKeyboardSecretToggle } from '@hooks/use-keyboard-secret-toggle';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { os } from '@utils/operating-system';

import { AiBuilderChat } from './ai-builder/ai-builder-chat';
import { zoomDuration } from './base-builder';

export const Controls = ({
  journeyId,
  isTemplate,
}: {
  journeyId?: string;
  isTemplate?: boolean;
}) => {
  const { fitView, zoomIn, zoomOut } = useReactFlow();
  const { t } = useScopedTranslate(locale);
  const [canSeeAiBuilder, setCanSeeAiBuilder] = useLocalStorageState({
    key: 'show-ai-builder',
    defaultValue: false,
  });

  useKeyboardSecretToggle({
    key: 'show-ai-builder',
    shortcut: 'eqqr',
    onSuccess: () => {
      setCanSeeAiBuilder((prev) => !prev);
    },
  });

  const [hasAiBuilderOpen, setHasAiBuilderOpen] = useLocalStorageState({
    key: 'has-ai-builder-open',
    defaultValue: true,
  });

  const isAiBuilderOpen = canSeeAiBuilder && hasAiBuilderOpen && !isTemplate && Boolean(journeyId);

  return (
    <>
      {isAiBuilderOpen && journeyId && (
        <Panel position="top-left" style={{ left: 0, top: 0, bottom: 0, width: 386 }}>
          <AiBuilderChat journeyShortId={journeyId} onClose={() => setHasAiBuilderOpen(false)} />
        </Panel>
      )}

      <Panel position="bottom-left" style={{ left: isAiBuilderOpen ? 404 : 16 }}>
        <Stack orientation="horizontal" space={2}>
          {!isAiBuilderOpen && canSeeAiBuilder && !isTemplate && journeyId && (
            <Button
              iconLeft={<IconAiGradient />}
              text={t('aiBuilder')}
              variant="secondary"
              onClick={() => setHasAiBuilderOpen(true)}
            />
          )}
          <PopoverButton
            placement="top-start"
            contentStyle={{ maxWidth: pxToRem(294) }}
            trigger={Button}
            triggerProps={{
              iconRight: <IconHelp />,
              variant: 'secondary',
            }}
          >
            <NavigationContainer>
              <Stack space={4}>
                <Text font="semibold14" text={t('navigation')} />
                <Stack orientation="horizontal" justify="space-between">
                  <Text font="semibold14" text={t('panKey')} />
                  <Text font="regular14" text={t('panLabel')} />
                </Stack>
                <Stack orientation="horizontal" justify="space-between">
                  <Text font="semibold14" text={t(`zoomScrollKey${os}`) + t('mouseWheel')} />
                  <Text font="regular14" text={t('zoomInOutLabel')} />
                </Stack>
                <Stack orientation="horizontal" justify="space-between">
                  <Text font="semibold14" text={t(`zoomKey${os}`) + t('plus')} />
                  <Text font="regular14" text={t('zoomInLabel')} />
                </Stack>
                <Stack orientation="horizontal" justify="space-between">
                  <Text font="semibold14" text={t(`zoomKey${os}`) + t('minus')} />
                  <Text font="regular14" text={t('zoomOutLabel')} />
                </Stack>
                <Stack orientation="horizontal" justify="space-between">
                  <Text font="semibold14" text={t(`zoomKey${os}`) + t('fitViewKey')} />
                  <Text font="regular14" text={t('fitViewLabel')} />
                </Stack>
              </Stack>
            </NavigationContainer>
          </PopoverButton>
          <Button
            iconRight={<IconZoomOut />}
            variant="secondary"
            id="zoom-out"
            onClick={() => zoomOut({ duration: zoomDuration })}
          />
          <Button
            iconRight={<IconZoomIn />}
            variant="secondary"
            id="zoom-in"
            onClick={() => zoomIn({ duration: zoomDuration })}
          />
          <Button
            iconRight={<IconZoomAll />}
            variant="secondary"
            onClick={() => fitView({ duration: zoomDuration, maxZoom: 1 })}
          />
        </Stack>
      </Panel>
    </>
  );
};

const NavigationContainer = styled.div({
  padding: pxToRem(8),
});
const locale = {
  en: {
    navigation: 'Navigation',
    panKey: 'Space + Drag',
    panLabel: 'Move canvas',
    zoomScrollKeyMacOS: '⌘ + ',
    zoomScrollKeyWindows: 'Ctrl + ',
    zoomScrollKeyLinux: 'Ctrl + ',
    zoomScrollKeyUnknown: 'Ctrl + ',
    zoomKeyMacOS: '⌘ + ',
    zoomKeyWindows: 'Ctrl + ',
    zoomKeyLinux: 'Ctrl + ',
    zoomKeyUnknown: 'Ctrl + ',
    mouseWheel: 'Mousewheel',
    zoomInOutLabel: 'Zoom In / Out',
    plus: 'Plus',
    zoomInLabel: 'Zoom In',
    minus: 'Minus',
    zoomOutLabel: 'Zoom Out',
    fitViewKey: '0 / Home',
    fitViewLabel: 'Fit view',
    aiBuilder: 'AI builder',
  },
};
